import {
  Button,
  FirstRunGuide,
  HelpTip,
  formatDate,
  guideStorageKey,
  useFirstRunGuide,
  type GuideStepDef,
} from '@dataroom/shared';
import { useMyQuestions, useSession } from '@/api/hooks';
import { buyerRooms } from '@/config';

/**
 * The buyer's four steps (exported for the tests). Browsing ticks from what the buyer
 * did (picked a folder, asked the room); asking a question from the room's own question
 * list; the last two are things to know, so they are acknowledged.
 */
export function buyerSteps(input: {
  /** ISO time the buyer's access ends; absent only for an older grant with no date on file. */
  accessEndsAt: string | undefined;
  browsed: boolean;
  hasAsked: boolean;
  onOpenQuestions: () => void;
}): GuideStepDef[] {
  const { accessEndsAt, browsed, hasAsked, onOpenQuestions } = input;
  const gotIt = (markDone: () => void) => (
    <Button size="sm" variant="outline" onClick={markDone}>
      Got it
    </Button>
  );
  return [
    {
      id: 'browse',
      title: 'Browse by folder, or ask the Data Room',
      description:
        'Pick a folder on the left, or type what you want to find, such as “show me the bank statements”.',
      done: browsed,
    },
    {
      id: 'question',
      title: 'Ask a question',
      description: 'Questions go privately to the deal team. Their answer comes back to you here.',
      done: hasAsked,
      action: () => (
        <Button size="sm" variant="outline" onClick={onOpenQuestions}>
          Open Questions
        </Button>
      ),
    },
    {
      id: 'access',
      title: accessEndsAt
        ? `Your access ends ${formatDate(accessEndsAt)}`
        : 'The deal team sets when your access ends',
      description: accessEndsAt
        ? 'The deal team can extend it.'
        : 'Ask them if you need to check your date.',
      action: gotIt,
    },
    {
      id: 'redaction',
      title: 'Account numbers are blacked out by design',
      description:
        'Documents show account numbers and similar identifiers redacted. That is how every copy in this room is shown.',
      action: gotIt,
    },
  ];
}

/** The buyer room's first-visit guide, "Find your way around". One per room, remembered per browser. */
export function BuyerGuide({
  roomId,
  onOpenQuestions,
}: {
  roomId: string;
  onOpenQuestions: () => void;
}) {
  const guide = useFirstRunGuide(guideStorageKey('buyer', roomId));
  const session = useSession();
  const questions = useMyQuestions(roomId);
  // Wait for what the steps are built from, so none flashes undone and ticks a moment later.
  if (!session.data || !questions.data) return null;
  const accessEndsAt = buyerRooms(session.data).find((r) => r.roomId === roomId)?.accessEndsAt;
  const steps = buyerSteps({
    accessEndsAt,
    browsed: guide.activity.has('ask') || guide.activity.has('folder'),
    hasAsked: questions.data.questions.length > 0,
    onOpenQuestions,
  });
  return (
    <FirstRunGuide
      guide={guide}
      title="Find your way around"
      intro={
        <>
          These steps tick off by themselves as you go. <HelpTip id="buyer.room.guide" />
        </>
      }
      ariaLabel="Buyer guide"
      steps={steps}
      resumeLabel="Show the room guide"
    />
  );
}
